<template>
  <div class="container">
    <bonus-title initial-tab-value="upcoming" />
    <div class="m-vx4">
      <div class="f">
        <div class="t m-rx1">Filters:</div>
        <donut-selector-multi-static v-model="typeFilterSelection" :options="typeFilterOptions" class="w-x40" />
      </div>
      <div class="t m-tx1">
        <span>You are currently </span>
        <template v-if="activeMilestoneTypes.length === 2">
          <span>sending {{ pointsNoun }} for both </span>
          <span class="bold">{{ pluralizer['anniversary'] }}</span>
          <span> and </span>
          <span class="bold">{{ pluralizer['birthday'] }}</span>
        </template>
        <template v-else-if="activeMilestoneTypes.length === 1">
          <span>sending {{ pointsNoun }} only for </span>
          <span class="bold">{{ pluralizer[activeMilestoneTypes[0]] }}</span>
        </template>
        <template v-else>
          <span>not sending any recurring annual {{ pointsNoun }}</span>
        </template>
        <span>. View your </span>
        <a href="/bonus/milestones" class="link">Annual Events</a>
        <span> to configure.</span>
      </div>
    </div>
    <!-- Table -->
    <donut-table :headers="tableHeaders" :data="tableData">
      <template #points="{ entry }">
        <div v-if="entry.points" v-emoji="entry.points" class="t" />
        <div v-else class="f-ac t gray">
          Not celebrated
          <i v-tooltip="entry.skipReason" class="mi md-16 m-l">info</i>
        </div>
      </template>
      <template #date="{ entry }">
        <div class="f">
          <div class="t">{{ entry.date }}</div>
          <i v-if="entry.adjustedForLeapYear" v-tooltip="'Date adjusted for leap year'" class="mi md-16 m-l gray">info</i>
        </div>
      </template>
      <template #user="{ entry }">
        <div class="f f-gap-8">
          <onboarding-membership-avatar :avatar="entry.avatar" />
          <div class="t">{{ entry.user }}</div>
        </div>
      </template>
    </donut-table>
    <div v-if="isFetching" class="f-jc"><donut-loading-dots /></div>
    <donut-empty
      v-else-if="!tableData.length"
      title="No Upcoming Events"
      description="Adjust filters or view your Annual Events to configure"
      image="nucleo/ghost"
      action-u-r-l="/bonus/milestones"
      action-label="Annual Events"
    />
  </div>
</template>

<script setup>
  import { ref, computed, inject, watch } from 'vue';
  import { useStore } from 'vuex';
  import { capitalize } from 'lodash';
  import useStringFormatting from 'composables/string-formatting';
  import useInfiniteScroll from 'composables/infinite-scroll';

  const $i18n = inject('$i18n');
  const { state, getters, dispatch } = useStore();
  const { toOrdinal } = useStringFormatting();

  // General
  const pointsNoun = $i18n.t('shoutouts.point', { count: 2 });
  const pluralizer = {
    anniversary: 'Anniversaries',
    birthday: 'Birthdays',
  };

  // Filtering
  const activeMilestoneTypes = computed(() => {
    const { anniversary, birthday } = state.bonusMilestoneConfigs;
    return [anniversary, birthday].filter((m) => m.active).map(m => m.milestone_type);
  });
  const typeFilterOptions = [{ value: 'anniversary', label: 'Anniversary' }, { value: 'birthday', label: 'Birthday' }];
  const typeFilterSelection = ref(typeFilterOptions.filter(o => activeMilestoneTypes.value.includes(o.value)));
  const typeFilterSelectionParam = computed(() => typeFilterSelection.value.map(o => o.value).join(','));

  // Data Collections
  const collectionKey = computed(() => typeFilterSelectionParam.value || 'none');
  const collections = ref({});
  const collection = computed(() => collections.value[collectionKey.value]);

  // Data Fetching
  const nextPage = computed(() => (collection.value ? collection.value.nextPage : 1));
  const isFetching = ref(false);
  const fetchData = () => {
    isFetching.value = true;
    const params = { page: nextPage.value };
    if (typeFilterSelectionParam.value) params.milestone_types = typeFilterSelectionParam.value;
    return dispatch('bonusMilestoneConfigs/fetchUpcomingDates', params)
      .then((data) => {
        if (collection.value) {
          collections.value[collectionKey.value] = { list: collection.value.list.concat(data.list), nextPage: data.nextPage };
        } else {
          collections.value[collectionKey.value] = data;
        }
        isFetching.value = false; // must be false before composable's .then() checks shouldFetchAtEndOfScroll
      })
      .catch(() => { isFetching.value = false; });
  };
  const shouldFetchAtEndOfScroll = computed(() => nextPage.value && !isFetching.value);
  const { fetchFirstPage } = useInfiniteScroll({ fetchData, shouldFetchAtEndOfScroll });
  watch(collectionKey, () => {
    if (!collection.value) fetchData();
  });

  // Table Rendering
  const tableHeaders = [
    { key: 'eventName', label: 'Milestone Event', weight: 3 },
    { key: 'points', label: state.pointsConfig.config.points_name_plural, weight: 2 },
    { key: 'date', label: 'Date', weight: 2 },
    { key: 'user', label: 'Recipient', weight: 3 },
  ];
  const tableData = computed(() => {
    const list = collection.value?.list || [];
    return list.map((event) => {
      let pointsPhrase = '';
      let skipReason;
      if (event.walletInactive) {
        skipReason = `This user's ${pointsNoun} are disabled.`;
      } else if (activeMilestoneTypes.value.includes(event.milestoneType)) {
        const numPoints = getters['bonusMilestoneConfigs/pointsForEvent'](event.milestoneType, event.ordinal);
        if (numPoints) {
          pointsPhrase = getters['pointsConfig/pointsPhraseFromConfig']({ points: numPoints, hideName: true });
        } else {
          // Only possible if using milestone anniversaries with no default.
          skipReason = `${toOrdinal(event.ordinal)} Anniversaries are not being celebrated. Turn them on in Annual Events.`;
        }
      } else {
        skipReason = `${pluralizer[event.milestoneType]} are not being celebrated. Turn them on in Annual Events.`;
      }
      let eventName = capitalize(event.milestoneType);
      if (event.ordinal) eventName = `${toOrdinal(event.ordinal)} ${eventName}`;
      return { ...event, eventName, points: pointsPhrase, skipReason };
    });
  });
</script>
